import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Space, Tag, Typography, message } from 'antd';
import { CheckOutlined, CloseOutlined, RollbackOutlined } from '@ant-design/icons';
// Default export = the /api/admin instance. `/approval-review/*` is mounted there,
// so the path must NOT be prefixed with /api. See frontend-api-instances.
import api from '../../services/api';

const { Text, Title } = Typography;
const { TextArea } = Input;

/**
 * The approval review, on the estimate's own screen.
 *
 * Owner requirement 2026-07-30: *"the estimate or work order should be able to
 * review changes not another separate page"*. Staff were sent to
 * `/owner/approval-review` to accept corrections for an estimate they were already
 * looking at, with no indication on the estimate itself that anything was pending.
 *
 * Reads `GET /approval-review/estimate/:id` — the per-estimate endpoint — rather
 * than filtering the shop-wide queue client-side.
 *
 * ── On "accept all" ────────────────────────────────────────────────────────
 *
 * Every change defaults to ACCEPT, so "Accept review" already applied all of the
 * customer's values; the queue page just never said so. This component states it
 * ("Accept all N changes") and adds the missing inverse — a bulk "Keep all ours" —
 * because a card with a dozen corrections had no way to reject them all without
 * twelve clicks.
 */

export interface ApprovalChange {
  id: number;
  target_type: 'customer' | 'vehicle';
  field_name: string;
  /** Human label, supplied by the server's `labelForField`. */
  label?: string | null;
  current_value: string | null;
  requested_value: string | null;
  status: string;
  review_note?: string | null;
}

export interface ApprovalSubmission {
  estimate_id: number;
  estimate_number: string;
  state: 'pending_customer' | 'submitted' | 'returned' | 'accepted';
  submitted_at: string | null;
  return_count: number;
  return_note: string | null;
}

/**
 * The server labels every change with `labelForField`, so there is deliberately no
 * label table here: a second copy would send a raw column name to a reviewer the
 * first time a field is added to the policy. De-snaking is the last resort only.
 */
const labelOf = (change: ApprovalChange): string =>
  change.label ||
  change.field_name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const shown = (v: string | null): string => (v === null || v === '' ? '—' : v);

export function EstimateApprovalReview({
  estimateId,
  onReviewed,
  readOnly = false,
}: {
  estimateId: number;
  /** Fired after a successful accept or return so the parent can refetch. */
  onReviewed?: () => void;
  /**
   * Read-only history. A work order only exists once the submission reached
   * `accepted` (estimates.ts refuses the conversion otherwise), so on a work
   * order there is never anything to decide — only the record of what the
   * customer corrected and which corrections were taken.
   */
  readOnly?: boolean;
}): React.ReactElement | null {
  const [submission, setSubmission] = useState<ApprovalSubmission | null>(null);
  const [changes, setChanges] = useState<ApprovalChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** change id -> accept. Absent means ACCEPT, matching the server's default. */
  const [decisions, setDecisions] = useState<Record<number, boolean>>({});
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [fieldNotes, setFieldNotes] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/approval-review/estimate/${estimateId}`);
      setSubmission(res.data?.submission ?? null);
      setChanges(Array.isArray(res.data?.changes) ? res.data.changes : []);
    } catch {
      // A failed read must not break the estimate screen around it.
      setSubmission(null);
      setChanges([]);
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => {
    load();
  }, [load]);

  // What gets RENDERED and what gets DECIDED are different sets.
  //
  // Read-only shows everything — the point there is the record. Interactive shows
  // rows still awaiting a decision plus rows already sent back, so a reviewer
  // looking at a bounced submission can see which fields were flagged and what
  // note the customer received. Only `pending` rows may carry a decision: sending
  // a decision for a `returned` row would silently re-resolve it.
  const rows = readOnly
    ? changes
    : changes.filter((c) => c.status === 'pending' || c.status === 'returned');
  const pending = rows.filter((c) => c.status === 'pending');
  const accepts = (id: number) => decisions[id] !== false;
  const acceptedCount = pending.filter((c) => accepts(c.id)).length;

  const setAll = (accept: boolean) =>
    setDecisions(Object.fromEntries(pending.map((c) => [c.id, accept])));

  const submitAccept = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/approval-review/${estimateId}/accept`, {
        decisions: pending.map((c) => ({ id: c.id, accept: accepts(c.id) })),
      });
      message.success('Customer details reviewed');
      onReviewed?.();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save that review.');
    } finally {
      setBusy(false);
    }
  };

  const submitReturn = async () => {
    if (!returnNote.trim()) {
      setError('Tell the customer what needs fixing before sending it back.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/approval-review/${estimateId}/return`, {
        return_note: returnNote.trim(),
        issues: pending
          .filter((c) => (fieldNotes[c.id] ?? '').trim())
          .map((c) => ({ id: c.id, note: fieldNotes[c.id].trim() })),
      });
      message.success('Sent back to the customer');
      setReturnOpen(false);
      setReturnNote('');
      onReviewed?.();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not send that back.');
    } finally {
      setBusy(false);
    }
  };

  // Nothing to show: no submission, or one with no pending corrections. Renders
  // null so the estimate screen is unchanged when there is nothing to review.
  if (loading) return null;
  if (!submission) return null;
  // A `submitted` submission with NO corrections still needs the panel: the
  // customer approved exactly what was on file, and G3 blocks conversion until a
  // human settles it. Anything else with nothing to show renders nothing.
  if (rows.length === 0 && !(submission.state === 'submitted' && !readOnly)) return null;

  const awaitingReview = submission.state === 'submitted' && !readOnly;

  return (
    <Card
      data-testid="estimate-approval-review"
      style={{ marginBottom: 16, borderColor: awaitingReview ? '#faad14' : undefined }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {readOnly ? 'Details the customer submitted' : 'Customer details to review'}
            {awaitingReview && (
              <Tag color="orange" style={{ marginLeft: 8 }} data-testid="review-state-tag">
                Awaiting review
              </Tag>
            )}
            {submission.state === 'returned' && (
              <Tag color="red" style={{ marginLeft: 8 }} data-testid="review-state-tag">
                Sent back
              </Tag>
            )}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {readOnly
              ? 'Reviewed already — shown here for the record.'
              : "Nothing here has been written to the customer's record yet."}
            {submission.return_count > 0 &&
              ` Returned to the customer ${submission.return_count} time${submission.return_count === 1 ? '' : 's'} already.`}
          </Text>
        </div>

        {error && <Alert type="error" showIcon message={error} />}

        {rows.length === 0 && (
          <Text type="secondary" data-testid="review-nothing-pending">
            They approved without changing anything. Accepting just settles it, and
            unblocks converting this to a work order.
          </Text>
        )}

        {awaitingReview && pending.length > 1 && (
          <Space wrap size={8}>
            <Button
              size="small"
              data-testid="accept-all-changes"
              onClick={() => setAll(true)}
              style={{ minHeight: 44 }}
            >
              Use all theirs
            </Button>
            <Button
              size="small"
              data-testid="reject-all-changes"
              onClick={() => setAll(false)}
              style={{ minHeight: 44 }}
            >
              Keep all ours
            </Button>
          </Space>
        )}

        {rows.map((change) => {
          // Read-only reflects what was DECIDED (the row's own status); interactive
          // reflects what the reviewer has picked but not yet submitted.
          //
          // The status vocabulary is 'pending' | 'approved' | 'rejected' (migration
          // 178) plus 'returned' from the return handler. It is NOT 'accepted' —
          // that is the *submission's* state, a different column on a different
          // table. Getting it wrong showed every accepted field as "Kept ours",
          // which is the exact opposite of what happened; caught live, so
          // approval_review_all_fields.test.ts now pins these literals.
          // A row is decidable only while it is still `pending` AND the submission
          // is awaiting review. Everything else is history: its own status is the
          // truth, not whatever the reviewer's unsent picks happen to be.
          const decidable = awaitingReview && change.status === 'pending';
          const accepted = decidable ? accepts(change.id) : change.status === 'approved';
          // Tint only on an explicit negative. A row still awaiting a decision is
          // not a rejection, so it must not read as one.
          const negative = decidable
            ? !accepted
            : change.status === 'rejected' || change.status === 'returned';
          return (
            <div
              key={change.id}
              data-testid={`review-change-${change.id}`}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 10,
                background: negative ? '#fff7f6' : '#ffffff',
              }}
            >
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                {labelOf(change)}
                {!decidable && change.status !== 'pending' && (
                  <Tag
                    color={accepted ? 'green' : change.status === 'returned' ? 'red' : 'default'}
                    style={{ marginLeft: 8 }}
                    data-testid={`review-outcome-${change.id}`}
                  >
                    {accepted ? 'Used theirs' : change.status === 'returned' ? 'Sent back' : 'Kept ours'}
                  </Tag>
                )}
              </Text>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    On file
                  </Text>
                  <Text delete={accepted} style={{ fontSize: 14, wordBreak: 'break-word' }}>
                    {shown(change.current_value)}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    Customer says
                  </Text>
                  <Text strong style={{ fontSize: 14, wordBreak: 'break-word' }}>
                    {shown(change.requested_value)}
                  </Text>
                </div>
              </div>
              {/* Only while the submission is actually awaiting review. A
                  `returned` submission still has pending rows, and showing the
                  toggles there gave a reviewer buttons with no way to submit them
                  — the accept action is gated on `submitted` server-side (409
                  SUBMISSION_NOT_AWAITING_REVIEW). Caught at 390px on estimate
                  6267, which was mid-return. */}
              {decidable && (
              <Space wrap size={8}>
                <Button
                  size="small"
                  type={accepted ? 'primary' : 'default'}
                  icon={<CheckOutlined />}
                  data-testid={`review-accept-${change.id}`}
                  aria-pressed={accepted}
                  onClick={() => setDecisions((p) => ({ ...p, [change.id]: true }))}
                  style={{ minHeight: 44, paddingInline: 12 }}
                >
                  Use theirs
                </Button>
                <Button
                  size="small"
                  danger={!accepted}
                  type={accepted ? 'default' : 'primary'}
                  icon={<CloseOutlined />}
                  data-testid={`review-reject-${change.id}`}
                  aria-pressed={!accepted}
                  onClick={() => setDecisions((p) => ({ ...p, [change.id]: false }))}
                  style={{ minHeight: 44, paddingInline: 12 }}
                >
                  Keep ours
                </Button>
              </Space>
              )}
              {change.review_note && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                  Note to customer: {change.review_note}
                </Text>
              )}
              {returnOpen && (
                <TextArea
                  data-testid={`review-field-note-${change.id}`}
                  placeholder={`What is wrong with ${labelOf(change)}?`}
                  value={fieldNotes[change.id] ?? ''}
                  onChange={(e) =>
                    setFieldNotes((p) => ({ ...p, [change.id]: e.target.value }))
                  }
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  style={{ marginTop: 8, fontSize: 16 }}
                />
              )}
            </div>
          );
        })}

        {returnOpen && (
          <TextArea
            data-testid="review-return-note"
            placeholder="What does the customer need to fix?"
            value={returnNote}
            onChange={(e) => setReturnNote(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 5 }}
            style={{ fontSize: 16 }}
          />
        )}

        {awaitingReview && (
          <Space wrap size={8}>
            <Button
              type="primary"
              loading={busy}
              data-testid="review-accept-submit"
              onClick={submitAccept}
              style={{ minHeight: 44 }}
            >
              {/* States plainly what pressing it does — the queue page's
                  "Accept review" hid that every change defaults to accepted. */}
              {pending.length === 0
                ? 'Accept review'
                : acceptedCount === pending.length
                  ? `Accept all ${pending.length} change${pending.length === 1 ? '' : 's'}`
                  : `Accept review (${acceptedCount} of ${pending.length})`}
            </Button>
            <Button
              danger
              icon={<RollbackOutlined />}
              loading={busy}
              data-testid="review-return-submit"
              onClick={() => (returnOpen ? submitReturn() : setReturnOpen(true))}
              style={{ minHeight: 44 }}
            >
              {returnOpen ? 'Send back now' : 'Send back to customer'}
            </Button>
            {returnOpen && (
              <Button
                size="small"
                onClick={() => setReturnOpen(false)}
                style={{ minHeight: 44 }}
              >
                Cancel
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
}

export default EstimateApprovalReview;
